import React, { createContext, useContext, useState } from 'react';
import { LanguageContext } from '../pages/LanguageContext';
import AlertModal from '../components/AlertModal';
import { ThemeContext } from '../pages/theme';

const ModalContext = createContext(null);

export const useModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within ModalProvider');
  return ctx;
};

export const ModalProvider = ({ children }) => {
  const [modal, setModal] = useState({ visible: false });
  const { language } = useContext(LanguageContext) || { language: 'en' };
  const { theme: appTheme } = useContext(ThemeContext) || { theme: 'light' };

  const modalTranslations = {
    en: {
      close: 'Close',
      notice: 'Notice',
      featureNotAvailable: 'This feature is not available for your organization.',
      requestSent: 'Request sent',
      requestFailed: 'Request failed',
      requestAccess: 'Request Access',
    },
    ar: {
      close: 'إغلاق',
      notice: 'تنبيه',
      featureNotAvailable: 'هذه الميزة غير متاحة لشركتك.',
      requestSent: 'تم إرسال الطلب',
      requestFailed: 'فشل الإرسال',
      requestAccess: 'طلب الوصول',
    }
  };

  const mt = modalTranslations[language] || modalTranslations.en;

  const showModal = ({ title, message, options = {}, onConfirm } = {}) => {
    const finalTitle = title || mt.notice;
    const finalMessage = message || mt.featureNotAvailable;
    setModal({ visible: true, title: finalTitle, message: finalMessage, options, onConfirm, loading: false, feedback: null });
  };

  const hideModal = () => setModal({ visible: false });

  const requestAccess = async (moduleName) => {
    if (!modal.options?.requestAccessEndpoint) return null;
    setModal((m) => ({ ...m, loading: true }));
    try {
      const clientName = localStorage.getItem('clientName') || '';
      const resp = await fetch(modal.options.requestAccessEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module: moduleName, clientName }),
      });
      const data = await resp.json();
      setModal((m) => ({ ...m, loading: false, feedback: data.message || mt.requestSent }));
      return data;
    } catch (err) {
      setModal((m) => ({ ...m, loading: false, feedback: mt.requestFailed }));
      return null;
    }
  };

  const value = { showModal, hideModal, modal, requestAccess };

  return (
    <ModalContext.Provider value={value}>
      {children}
      {modal.visible && (
        <AlertModal
          open={modal.visible}
          title={modal.title || mt.notice}
          message={modal.message ? (modal.feedback ? `${modal.message}\n\n${modal.feedback}` : modal.message) : mt.featureNotAvailable}
          variant={modal.options?.variant || 'info'}
          showCancel={!!modal.options?.showCancel}
          onConfirm={() => {
            if (typeof modal.onConfirm === 'function') modal.onConfirm();
            hideModal();
          }}
          onClose={() => hideModal()}
          theme={appTheme || 'light'}
          language={language}
        />
      )}
    </ModalContext.Provider>
  );
};

export default ModalContext;
